import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNow,
  differenceInMinutes,
  isToday,
  isYesterday,
  isThisWeek,
  getDay,
} from 'date-fns';
import { zhCN, enUS, pt, zhTW } from 'date-fns/locale';
import dayjs from 'dayjs';

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat = 'h:mm a') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, dateFormat);
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @param {string} [locale='zh_CN'] - Locale for formatting.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (
  time,
  dateFormat = 'MMM d, yyyy',
  locale = 'zh_CN'
) => {
  const messageTime = fromUnixTime(time);
  const now = new Date();

  const localeMap = {
    zh_CN: zhCN,
    en: enUS,
    pt: pt,
    zh_TW: zhTW,
    // 可以根据需要添加更多语言
  };

  const selectedLocale = localeMap[locale] || zhCN;

  const messageDate = format(messageTime, dateFormat, {
    locale: selectedLocale,
  });
  if (!isSameYear(messageTime, now)) {
    // return format(messageTime, 'LLL d y, h:mm a', { locale: selectedLocale });
  }
  return messageDate;
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @param {string} [locale='zh-CN'] - Locale for formatting.
 * @returns {string} Relative time string.
 */
export const dynamicTime = (time, locale = 'zh-CN') => {
  // 验证时间戳有效性
  if (
    time === null ||
    time === undefined ||
    time === '' ||
    Number.isNaN(time)
  ) {
    return '';
  }

  const unixTime = fromUnixTime(time);

  // 验证转换后的日期有效性
  if (Number.isNaN(unixTime.getTime())) {
    return '';
  }

  const localeMap = {
    zh_CN: zhCN,
    en: enUS,
    pt: pt,
    zh_TW: zhTW,
    // 可以根据需要添加更多语言
  };

  return formatDistanceToNow(unixTime, {
    addSuffix: true,
    locale: localeMap[locale] || zhCN, // 如果找不到对应的语言包，默认使用中文
  });
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df = 'MMM d, yyyy') => {
  const unixTime = fromUnixTime(time);
  return format(unixTime, df);
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) => {
  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const suffix = withAgo ? ' ago' : '';
  const timeMappings = {
    'less than a minute ago': 'now',
    'a minute ago': `1m${suffix}`,
    'an hour ago': `1h${suffix}`,
    'a day ago': `1d${suffix}`,
    'a month ago': `1mo${suffix}`,
    'a year ago': `1y${suffix}`,
  };
  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }
  const convertToShortTime = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `m${suffix}`)
    .replace(' minutes ago', `m${suffix}`)
    .replace(' hour ago', `h${suffix}`)
    .replace(' hours ago', `h${suffix}`)
    .replace(' day ago', `d${suffix}`)
    .replace(' days ago', `d${suffix}`)
    .replace(' month ago', `mo${suffix}`)
    .replace(' months ago', `mo${suffix}`)
    .replace(' year ago', `y${suffix}`)
    .replace(' years ago', `y${suffix}`);
  return convertToShortTime;
};

// 本地化渲染时间
export const renderLocaleDateTime = time => {
  return dayjs(time).format('YYYY-MM-DD HH:mm:ss');
};

/**
 * 智能格式化时间戳
 * @param {number} timestamp - Unix时间戳
 * @param {string} locale - 语言设置，默认为中文
 * @returns {string} 格式化后的时间字符串
 */
export const smartTimeFormat = (timestamp, locale = 'zh_CN') => {
  // 验证时间戳有效性（包括 NaN 检查）
  if (
    timestamp === null ||
    timestamp === undefined ||
    timestamp === '' ||
    Number.isNaN(timestamp)
  ) {
    return '';
  }

  const date = fromUnixTime(timestamp);

  // 验证转换后的日期有效性
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const now = new Date();
  const diffInMinutes = differenceInMinutes(now, date);

  // 语言文本映射
  const i18n = {
    just_now: {
      zh_CN: '刚刚',
      zh_TW: '剛剛',
      pt: 'agora mesmo',
      en: 'just now',
    },
    minutes_ago: {
      zh_CN: minute => `${minute}分钟前`,
      zh_TW: minute => `${minute}分鐘前`,
      pt: minute => `${minute} ${minute === 1 ? 'minuto' : 'minutos'} atrás`,
      en: minute => `${minute} ${minute === 1 ? 'minute' : 'minutes'} ago`,
    },
    yesterday: {
      zh_CN: '昨天',
      zh_TW: '昨天',
      pt: 'Ontem',
      en: 'Yesterday',
    },
    weekdays: {
      zh_CN: [
        '星期日',
        '星期一',
        '星期二',
        '星期三',
        '星期四',
        '星期五',
        '星期六',
      ],
      zh_TW: [
        '星期日',
        '星期一',
        '星期二',
        '星期三',
        '星期四',
        '星期五',
        '星期六',
      ],
      en: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      pt: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
    },
  };

  // 获取对应语言的文本，如果没有则使用英文或中文作为后备
  const getText = (key, param) => {
    const langTexts = i18n[key];
    if (!langTexts) return '';

    // 处理函数类型的文本（如分钟前的格式化）
    if (typeof langTexts[locale] === 'function' && param !== undefined) {
      return langTexts[locale](param);
    }

    // 处理普通文本或数组
    return langTexts[locale] || langTexts.en || langTexts.zh_CN;
  };

  // 小于1分钟显示"刚刚"
  if (diffInMinutes < 1) {
    return getText('just_now');
  }

  // 小于1小时显示"xx分钟前"
  if (diffInMinutes < 60) {
    return getText('minutes_ago', diffInMinutes);
  }

  // 今天内显示具体时间 "HH:mm"
  if (isToday(date)) {
    return format(date, 'HH:mm');
  }

  // 如果是昨天，显示"昨天"
  if (isYesterday(date)) {
    return getText('yesterday');
  }

  // 本周内显示星期几
  if (isThisWeek(date)) {
    const dayOfWeek = getDay(date);
    const weekdays = getText('weekdays');

    // 如果weekdays是数组才取值，否则可能是后备字符串
    return Array.isArray(weekdays) ? weekdays[dayOfWeek] : weekdays;
  }

  // 超过本周显示日期 "yyyy/MM/dd"
  return format(date, 'yyyy/MM/dd');
};

/**
 * 格式化时间戳为年月日时分秒和星期几
 * @param {number|string} timestamp - 时间戳
 * @param {string} locale - 语言设置，默认为中文
 * @returns {string} 格式化后的时间字符串，格式为: YYYY-MM-DD HH:mm:ss 星期X
 */
export const formatDateTimeWithWeekday = (timestamp, locale = 'zh_CN') => {
  if (!timestamp) return '';

  const date =
    typeof timestamp === 'number'
      ? fromUnixTime(timestamp)
      : new Date(timestamp);

  // 语言对应的星期几文本
  const weekdayMap = {
    zh_CN: [
      '星期日',
      '星期一',
      '星期二',
      '星期三',
      '星期四',
      '星期五',
      '星期六',
    ],
    zh_TW: [
      '星期日',
      '星期一',
      '星期二',
      '星期三',
      '星期四',
      '星期五',
      '星期六',
    ],
    en: [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ],
    pt: [
      'Domingo',
      'Segunda-feira',
      'Terça-feira',
      'Quarta-feira',
      'Quinta-feira',
      'Sexta-feira',
      'Sábado',
    ],
  };

  // 获取对应语言的星期几文本
  const weekdays = weekdayMap[locale] || weekdayMap.zh_CN;
  const weekday = weekdays[date.getDay()];

  // 使用date-fns格式化日期时间
  const localeMap = {
    zh_CN: zhCN,
    zh_TW: zhTW,
    en: enUS,
    pt: pt,
  };

  const dateTimeStr = format(date, 'yyyy-MM-dd HH:mm:ss', {
    locale: localeMap[locale] || localeMap.zh_CN,
  });

  return `${dateTimeStr} ${weekday}`;
};
